import {ReactElement, useEffect, useRef, useState} from "react";
import {Field, FileUpload, Input, Label, Message} from "@zendeskgarden/react-forms";
import {
    AcquisitionPropertyTemplateName,
    useGenerateAcquisitionRentRollFileUploadUrlMutation,
    useProcessAcquisitionRentRollFileMutation
} from "../../__generated__/generated_types";
import {toast} from "react-toastify";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Separator} from "@zendeskgarden/react-dropdowns";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../constants/Colors";
import {useFetchTemplateCsv} from "./logic/useFetchTemplateCsv";

export interface AcquisitionPropertyRentRollModalProps {
    propertyId: string;
    onModalClose: () => void;
    jobCreated: (jobId: string, propertyId: string) => void;
}

export function AcquisitionPropertyRentRollModal(props: AcquisitionPropertyRentRollModalProps): ReactElement {
    const {propertyId, onModalClose} = props;

    const [rentRollFile, setRentRollFile] = useState<File | undefined>(undefined);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const rentRollInputRef = useRef<HTMLInputElement | null>(null);

    const [generateUrl, {loading: loadingUrl, data: urlData}] = useGenerateAcquisitionRentRollFileUploadUrlMutation({
        notifyOnNetworkStatusChange: true
    });

    const [processRentRoll, {loading: loadingProcess}] = useProcessAcquisitionRentRollFileMutation({
        notifyOnNetworkStatusChange: true
    });

    const {fetchTemplate} = useFetchTemplateCsv("rentroll");

    useEffect(() => {
        if(urlData && !loadingUrl && isUploading) {
            uploadFiles();
        }
    }, [urlData, loadingUrl]);

    const handleRentRollChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if(event.target.files && event.target.files.length > 0) {
            setRentRollFile(event.target.files[0]);
        }
    };

    const reset = () => {
        setRentRollFile(undefined);
        setIsUploading(false);
        onModalClose();
    };

    const uploadFiles = () => {
        if(!urlData || !rentRollFile) {
            setIsUploading(false);
            return;
        }

        // Get URLs and keys
        const rentRollUrl = urlData.generateAcquisitionRentRollFileUploadUrl.signedUrl;
        const rentRollKey = urlData.generateAcquisitionRentRollFileUploadUrl.key;

        // Create array of upload promises
        fetch(rentRollUrl, {
            method: 'PUT',
            body: rentRollFile,
            headers: {
                'Content-Type': rentRollFile.type,
            }
        })
        .then(() => {
            return processRentRoll({
                variables: {
                    propertyId,
                    fileKey: rentRollKey
                }
            });
        })
        .then(res => {
            if(res.data?.processAcquisitionRentRollFile) {
                props.jobCreated(res.data.processAcquisitionRentRollFile.id, propertyId);
                toast.success("Rent roll uploaded successfully.");
                reset();
            }
        })
        .catch((error) => {
            console.error("Error uploading file:", error);
            toast.error("Error uploading file");
        })
        .finally(() => {
            setIsUploading(false);
        });
    };

    const handleSubmit = () => {
        if(!rentRollFile) {
            toast.error("Please select a rent roll file.");
            return;
        }

        setIsUploading(true);
        generateUrl({
            variables: {
                propertyId
            }
        });
    };

    const renderSize = (size: number): string => {
        let i = 0;
        while(size > 1024 && i < 3) {
            size /= 1024;
            i++;
        }

        if(i === 0) {
            return `${size} bytes`;
        } else if(i === 1) {
            return `${size.toFixed(2)} KB`;
        } else {
            return `${size.toFixed(2)} MB`;
        }
    };

    return (
        <Modal onClose={reset}>
            <Header>Upload Property Files</Header>
            <Body>
                <form>
                    <Field>
                        <Label>Rent Roll File</Label>
                        <FileUpload
                                style={{display: !rentRollFile ? "block" : "none"}}
                                role="button"
                                onClick={() => {
                                    if(rentRollInputRef) {
                                        rentRollInputRef.current?.click();
                                    }
                                }}
                        >
                            <Message>Select Rent Roll CSV</Message>
                            <Input ref={rentRollInputRef} style={{display: "none"}} type="file"
                                   accept=".csv, .xls, .xlsx" onChange={handleRentRollChange}/>
                        </FileUpload>
                        {rentRollFile &&
                                <div style={{fontSize: "0.9rem", textAlign: "center"}}>
                                    {rentRollFile.name} ({renderSize(rentRollFile.size)})
                                    <Button isBasic onClick={() => {
                                        setRentRollFile(undefined);
                                        if(rentRollInputRef.current) {
                                            const input = rentRollInputRef.current;
                                            if(input.files && input.files.length > 0) {
                                                input.value = "";
                                            }
                                        }
                                    }}>Remove</Button>
                                </div>
                        }
                    </Field>
                </form>
                <div style={{marginTop: "0.5rem"}}><Button isLink onClick={() => fetchTemplate(propertyId, AcquisitionPropertyTemplateName.RentRoll)}>Download Rent Roll Template</Button></div>
            </Body>
            <Separator/>
            <Footer>
                <FooterItem>
                    <Button onClick={reset} isBasic disabled={isUploading || loadingUrl || loadingProcess}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button isPrimary onClick={handleSubmit}
                            disabled={!rentRollFile || isUploading || loadingUrl || loadingProcess}>
                        {
                            (isUploading || loadingUrl || loadingProcess)
                                    ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                    : <span>Upload</span>
                        }
                    </Button>
                </FooterItem>
            </Footer>
            <Close aria-label="Close modal"/>
        </Modal>
    );
}